import { ALERT_SOURCE_FIELD_KEYS, } from '../contracts/alerts';
import { PolicyKey } from '../contracts/policies';
import { ruleObjectSchemasByPolicy } from '../contracts/policy-exceptions';
/** Tooltip for a per-field copy action that has no real value to copy. */
export const COPY_FIELD_NO_VALUE_REASON = 'No value to copy';
/**
 * The Allow/Block list policy each alert-entity kind maps to, or null when the
 * kind carries no allow/block entity at all.
 *
 * A total map rather than the ternary this used to be (KIR-4443). With three
 * list policies a chain of ternaries silently routes every unmatched kind to
 * whichever branch is last — that is how `pii`, which has no entity, would have
 * been answered `mcp-server-sources`. Being total means a new kind is a
 * compile error here rather than a wrong answer at runtime.
 */
const POLICY_KEY_BY_SOURCE_KIND = {
    extension: PolicyKey.ExtensionsManagement,
    mcp: PolicyKey.McpServerSources,
    package: PolicyKey.PackagesManagement,
    // PII alerts carry only a redacted content sample — there is no entity to
    // allow or block.
    pii: null,
};
/**
 * The Allow/Block list policy an alert's entity maps to, derived purely from the
 * source-detail `kind`, or null when the kind has no such entity.
 *
 * A detection alert (e.g. an `extension-security` or `malicious-packages`
 * finding) still adds its entity to the corresponding LIST policy — the button
 * always targets the list policy, never the alert's own detection policy
 * (KIR-3558).
 */
export function sourceDetailsPolicyKey(sourceDetails) {
    return POLICY_KEY_BY_SOURCE_KIND[sourceDetails.kind];
}
/**
 * The rule-object keys, in order, that make up an Allow/Block identity for each
 * entity kind. Mirrors `extensionsManagementRuleSchema` /
 * `mcpServerSourcesRuleSchema`.
 */
const RULE_KEYS_BY_KIND = {
    extension: [
        ALERT_SOURCE_FIELD_KEYS.NAME,
        ALERT_SOURCE_FIELD_KEYS.PUBLISHER,
        ALERT_SOURCE_FIELD_KEYS.VERSION,
    ],
    mcp: [
        ALERT_SOURCE_FIELD_KEYS.NAME,
        ALERT_SOURCE_FIELD_KEYS.PUBLISHER,
        ALERT_SOURCE_FIELD_KEYS.TYPE,
    ],
    // KIR-4443: exactly the two fields a package rule matches on. Version is
    // shown on the alert but is not part of the rule.
    package: [ALERT_SOURCE_FIELD_KEYS.NAME, ALERT_SOURCE_FIELD_KEYS.ECOSYSTEM],
    // PII alerts carry no allow/block entity — only a redacted content sample.
    pii: [],
};
/**
 * The bullet character the backend mask token is built from (`MASK_TOKEN =
 * '••••'`). We can't import the backend constant here (this helper is shared,
 * backend-free), so we key off the character directly when locating the start
 * of a redacted fragment. Kept in lock-step with `apps/backend/src/domain/
 * findings/source-masking.ts`.
 */
const MASK_BULLET = '\u2022';
/**
 * A prefix that already anchors on a real host, so appending `*` yields a rule
 * whose boundary the Allow/Block matcher accepts. Either an explicit
 * `scheme://host/…` or a scheme-less dotted `host[:port]/…`. The trailing `/`
 * is required — it is the boundary character both the permissive block-list
 * matcher and the strict allow-list matcher (KIR-2114) treat as valid, so a
 * `prefix/*` rule matches `prefix/<anything>` on both lists.
 */
const SCHEME_HOST_PREFIX_RE = /^[a-z][a-z0-9+.-]*:\/\/[^/]+\//i;
const SCHEMELESS_HOST_PREFIX_RE = /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}(:\d+)?\//i;
function isHostAnchoredPrefix(prefix) {
    return (SCHEME_HOST_PREFIX_RE.test(prefix) || SCHEMELESS_HOST_PREFIX_RE.test(prefix));
}
/** Earliest index of any of `needles` in `value`, or -1 when none appear. */
function earliestIndexOf(value, needles) {
    let min = -1;
    for (const needle of needles) {
        const idx = value.indexOf(needle);
        if (idx !== -1 && (min === -1 || idx < min))
            min = idx;
    }
    return min;
}
/**
 * Turn a masked URL value into a host-anchored prefix wildcard, or `null` when
 * no safe anchor exists.
 *
 * A masked remote MCP publisher (e.g. `https://mcp.acme.com/sse?••••`) has had
 * its secret query/fragment redacted, so the literal value no longer equals the
 * real URL the matcher compares against at scan time — storing it verbatim is a
 * dead rule, and dropping the field entirely leaves the rule keyed only on the
 * (spoofable) name. Instead we keep the non-secret prefix — scheme + host + as
 * much path as precedes the redaction — cut back to the last `/` boundary, and
 * append `*`:
 *
 *   `https://mcp.acme.com/sse?••••`        -> `https://mcp.acme.com/*`
 *   `https://mcp.acme.com/api/v1/sse?••••` -> `https://mcp.acme.com/api/v1/*`
 *
 * This matches the real URL on BOTH lists (a `/`-terminated prefix is an
 * accepted boundary for the strict allow-list matcher, KIR-2114, and the
 * permissive block-list matcher) while never persisting the secret. It also
 * keeps a host constraint so the entry can't be satisfied by name alone.
 *
 * Returns `null` when the redaction starts before a usable host/path boundary
 * (secret in the host itself, or a query attached directly to the host with no
 * path, or no visible redaction boundary at all) — the caller then surfaces the
 * action as unavailable rather than emitting an over-broad or dead rule.
 */
function maskedPrefixWildcard(value) {
    // Where the redaction begins: a URL query/fragment delimiter or the mask
    // bullet itself. Everything from here on is secret/unusable -> wildcard tail.
    const markerIdx = earliestIndexOf(value, ['?', '#', MASK_BULLET]);
    if (markerIdx === -1)
        return null;
    const head = value.slice(0, markerIdx);
    const lastSlash = head.lastIndexOf('/');
    if (lastSlash === -1)
        return null;
    const prefix = head.slice(0, lastSlash + 1);
    if (!isHostAnchoredPrefix(prefix))
        return null;
    return `${prefix}*`;
}
/**
 * The copy-into-editor value of a source field. Uses `allowBlockValue` (the
 * normalized matcher value), never `detected` or the "Unknown" placeholder — so
 * the produced rule matches exactly what the Allow/Block matcher compares
 * against.
 *
 * - `skip` — no real value (unknown/empty); the field is omitted from the rule.
 * - `value` — a usable rule value. For a `masked` field this is a host-anchored
 *   prefix wildcard (see {@link maskedPrefixWildcard}) with `wildcard: true`.
 * - `unavailable` — the field is masked but cannot be safely represented, so no
 *   rule should be built at all (the action is surfaced as disabled, not
 *   silently narrowed to the remaining fields).
 */
function evaluateField(field) {
    if (!field || field.isUnknown)
        return { kind: 'skip' };
    const raw = field.allowBlockValue;
    if (raw == null || raw.trim().length === 0)
        return { kind: 'skip' };
    if (!field.masked)
        return { kind: 'value', value: raw, wildcard: false };
    const wildcard = maskedPrefixWildcard(raw.trim());
    if (wildcard != null)
        return { kind: 'value', value: wildcard, wildcard: true };
    return { kind: 'unavailable' };
}
/**
 * Evaluate an alert's KIR-3529 `sourceDetails` into an add-to-list rule and its
 * capability (KIR-3558). All surfaced known fields are used (extension
 * name/publisher/version; MCP name/publisher/type). A masked value (e.g. a
 * secret-bearing remote MCP publisher URL) becomes a host-anchored prefix
 * wildcard when one can be built (see {@link maskedPrefixWildcard}); when it
 * can't, the whole evaluation is `masked` rather than silently falling back to
 * the remaining (weaker, spoofable) fields. The result is validated against the
 * per-policy rule schema.
 *
 * Shared by the frontend (button gating + request payload) and the backend
 * (revalidation) so the added rule is the exact matcher identity the alert
 * surfaces.
 */
export function evaluateSourceDetailsRule(sourceDetails) {
    // PII alerts have no allow/block entity to build a rule from (KIR-3034).
    // Kinds with no allow/block entity (PII today) resolve to a null policy key.
    // Checking the map rather than the kind keeps this in step with
    // POLICY_KEY_BY_SOURCE_KIND instead of duplicating its knowledge.
    const policyKey = sourceDetailsPolicyKey(sourceDetails);
    if (policyKey === null || !('fields' in sourceDetails)) {
        return { capability: 'unavailable', rule: null };
    }
    const ruleObject = {};
    let usesWildcard = false;
    for (const key of RULE_KEYS_BY_KIND[sourceDetails.kind]) {
        const result = evaluateField(sourceDetails.fields.find(field => field.key === key));
        if (result.kind === 'unavailable') {
            return { capability: 'masked', rule: null };
        }
        if (result.kind === 'value') {
            ruleObject[key] = result.value;
            if (result.wildcard)
                usesWildcard = true;
        }
    }
    // Keyed off the resolved policy rather than the source kind, so the rule is
    // validated by exactly the schema the API will persist it under.
    const schema = ruleObjectSchemasByPolicy[policyKey];
    if (!schema || !schema.safeParse(ruleObject).success) {
        return { capability: 'unavailable', rule: null };
    }
    return {
        capability: 'available',
        rule: {
            policyKey,
            ruleObject,
            usesWildcard,
        },
    };
}
/**
 * Convenience wrapper over {@link evaluateSourceDetailsRule} returning the rule
 * when one can be built (`capability === 'available'`) and `null` otherwise.
 */
export function sourceDetailsToRuleObject(sourceDetails) {
    const evaluation = evaluateSourceDetailsRule(sourceDetails);
    return evaluation.capability === 'available' ? evaluation.rule : null;
}
/**
 * Whether an alert surfaces a list-capable entity — i.e. a valid Allow/Block
 * rule can be built from its `sourceDetails`. The single source of truth for
 * showing the "Add to Allow/Block List" action (KIR-3558).
 */
export function isListCapableAlert(sourceDetails) {
    return (sourceDetails !== undefined &&
        sourceDetailsToRuleObject(sourceDetails) !== null);
}
/**
 * Whether the alert's source details carry a known entity Name. The Name is the
 * primary identifier of the Extension / MCP server; when it is Unknown the
 * "Detected on client" panel would be an all-placeholder block (Unknown name +
 * usually Unknown publisher/type), so callers suppress the whole section rather
 * than render a section that identifies nothing (KIR-3529).
 */
export function hasKnownEntityName(sourceDetails) {
    if (!sourceDetails)
        return false;
    if (sourceDetails.kind === 'pii')
        return false; // no entity, only a sample
    const nameField = sourceDetails.fields.find(field => field.key === ALERT_SOURCE_FIELD_KEYS.NAME);
    if (!nameField || nameField.isUnknown)
        return false;
    const value = nameField.allowBlockValue ?? nameField.detected;
    return value != null && value.trim().length > 0;
}
/**
 * Neutral stand-in for an entity whose Name is unknown, per kind. A total map
 * for the same reason as {@link POLICY_KEY_BY_SOURCE_KIND}: an else-branch
 * default is how a package once read "this server".
 */
const ENTITY_FALLBACK_NAME = {
    extension: 'this extension',
    mcp: 'this server',
    package: 'this package',
    pii: 'this content',
};
/**
 * Human-readable name of the entity behind an alert, for confirmation copy
 * ("Add [name] to the … list?"). Prefers the `detected` value (what the user
 * saw in the alert) and falls back to the normalized `allowBlockValue`; returns
 * a neutral fallback when the name field carries no real value (KIR-3558).
 */
export function sourceDetailsEntityName(sourceDetails) {
    if (sourceDetails.kind === 'pii')
        return ENTITY_FALLBACK_NAME.pii;
    const nameField = sourceDetails.fields.find(field => field.key === ALERT_SOURCE_FIELD_KEYS.NAME);
    if (nameField && !nameField.isUnknown) {
        const value = nameField.detected ?? nameField.allowBlockValue;
        if (value != null && value.trim().length > 0)
            return value.trim();
    }
    return ENTITY_FALLBACK_NAME[sourceDetails.kind];
}
/**
 * Plural entity-type label per kind, matching the Allow/Block list section
 * names on the Policies screen. PII has no list; its label is never shown,
 * because a PII alert is never list-capable.
 */
const ENTITY_LIST_LABEL = {
    extension: 'Extensions',
    mcp: 'MCP Servers',
    package: 'Packages',
    pii: 'Content',
};
/**
 * Plural entity-type label used in success copy — matches the Allow/Block list
 * section names on the Policies screen ("Extensions" / "MCP Servers" /
 * "Packages").
 */
export function sourceDetailsEntityListLabel(sourceDetails) {
    return ENTITY_LIST_LABEL[sourceDetails.kind];
}
//# sourceMappingURL=source-details.js.map